import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  Grid
} from "@mui/material";
// import { Edit } from "@mui/icons-material";
import { updateSilverLoanApplication } from "../api";
import { Edit, CalendarToday } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatDateToDDMMYYYY, isDateField as isDateFieldUtil } from '../utils/dateHelpers';
import ConfidenceBadge from './ConfidenceBadge';

dayjs.extend(customParseFormat);

const ExtractedData = ({ data, saveAdapter, onSaved, confidenceScores }) => {
  const [formData, setFormData] = useState({});
  const editButtonClickRef = useRef(false);
  const [errors, setErrors] = useState({}); //  Track validation errors
  const [savedToast, setSavedToast] = useState(null); // { field, label, value }
  //  FIX: Removed forceRender state as it was causing TextField recreation

  const [editingField, setEditingField] = useState(null);
  const [highlightField, setHighlightField] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(null);

  //  FIX: Use a ref object to store refs for all dynamic fields
  const inputRefs = useRef({});

  //  FIX: Track the current data identity to detect when we switch documents
  //  FIX: Track the current data identity to detect when we switch documents
  const currentDataIdentity = useRef(null);

  // ✅ FIX: Track if form has been populated at least once for current document
  const formPopulatedRef = useRef(false);

  // ✅ FIX: Store formData snapshot per document identity to restore on revisit
  const formDataSnapshotRef = useRef({});

  const editedFields = useRef(new Set());
  const lastSavedRef = useRef({}); // { [field]: value } — tracks last successfully saved value per field

  //  FIX: Use refs to avoid stale closure in saveField
  const dataRef = useRef(data);
  const saveAdapterRef = useRef(saveAdapter);
  const onSavedRef = useRef(onSaved);

  // Debug: Log confidence scores when they change
  useEffect(() => {
    console.log('🎯 [CONFIDENCE SCORES] Received:', confidenceScores);
    if (confidenceScores) {
      console.log('🎯 [CONFIDENCE SCORES] Fields with scores:', Object.keys(confidenceScores));
      console.log('🎯 [CONFIDENCE SCORES] Sample values:', confidenceScores);
    }
  }, [confidenceScores]);

  //updated dipali 18/11
  const fieldLabels = {
    name: "Name",
    dateOfBirth: "Date of Birth",
    gender: "Gender",
    aadharNumber: "Aadhar Number",
    address: "Address",
    facilityAmount: "Loan Amount"  // Add this line
  };

  const formatLabel = (key) => {
    if (!key) return '';
    const spaced = key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();
    return spaced
      .split(' ')
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
      .join(' ');
  };

  //  Comprehensive validation rules for all fields
  const validateField = (fieldName, value) => {
    if (!value || value === '') return null; // Empty is allowed (not required)

    const fieldLower = fieldName.toLowerCase();

    //  PRIORITY 0: Date fields - allow date format with slashes
    // Check if this is a date field (dob, date, etc.)
    if (isDateFieldUtil(fieldName)) {
      // Allow date formats: DD/MM/YYYY, MM/DD/YYYY, or similar
      // Also allow ISO format dates that might come from backend
      const datePattern = /^(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})|(\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2})$/;
      if (!datePattern.test(value)) {
        return 'Invalid date format (use DD/MM/YYYY)';
      }
      return null;
    }

    //  PRIORITY 1: PAN format (check BEFORE numeric validation)
    // PAN fields have "number" keyword which conflicts with numeric validation
    if (fieldLower.includes('pan') && !fieldLower.includes('company') && !fieldLower.includes('name')) {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i;
      const isValid = panRegex.test(value);
      console.log(`🔍 PAN Validation:`, { field: fieldName, value, isValid, length: value.length });
      if (!isValid) {
        return 'Invalid PAN format (e.g., ABCDE1234F)';
      }
      return null;
    }

    //  PRIORITY 2: Aadhaar format (check BEFORE numeric validation)
    // Aadhaar fields have "number" keyword which conflicts with numeric validation
    if (fieldLower.includes('aadhaar') || fieldLower.includes('aadhar')) {
      const cleanValue = String(value).replace(/\s/g, '');
      if (!/^\d{12}$/.test(cleanValue)) {
        return 'Aadhaar must be 12 digits';
      }
      return null;
    }

    //  PRIORITY 3: Phone format (check BEFORE numeric validation)
    if (fieldLower.includes('phone') || fieldLower.includes('mobile')) {
      const cleanValue = String(value).replace(/[\s-]/g, '');
      if (!/^\d{10}$/.test(cleanValue)) {
        return 'Phone must be 10 digits';
      }
      return null;
    }

    //  PRIORITY 4: Form Number - alphanumeric with hyphens (check BEFORE numeric validation)
    // Form fields have "number" keyword which conflicts with numeric validation
    if (fieldLower.includes('form') && fieldLower.includes('number')) {
      if (!/^[A-Z0-9-]+$/i.test(value)) {
        return 'Only alphanumeric and hyphens allowed';
      }
      return null;
    }

    //  PRIORITY 5: Acknowledgement Number - alphanumeric (check BEFORE numeric validation)
    // Acknowledgement fields have "number" keyword which conflicts with numeric validation
    if (fieldLower.includes('acknowledgement')) {
      // Allow purely numeric acknowledgement numbers (like ITR acknowledgement numbers)
      // or alphanumeric acknowledgement numbers
      if (!/^[A-Z0-9]+$/i.test(value)) {
        return 'Only alphanumeric characters allowed';
      }
      return null;
    }

    //  PRIORITY 6: NetWorth fields - allow numbers with units like "Lacs", "Crores" (check BEFORE numeric validation)
    // NetWorth fields have keywords like "total", "assets" that conflict with numeric validation
    if (fieldLower.includes('assets') || fieldLower.includes('liabilities') || fieldLower.includes('networth') || fieldLower.includes('net worth')) {
      // Allow numbers with units like "123.64 Lacs", "5.5 Crores", etc.
      let cleanValue = String(value).trim();

      // Remove units like "Lacs", "Crores", "Lakhs", "Cr", etc.
      cleanValue = cleanValue.replace(/\s*(lacs?|crores?|lakhs?|cr\.?|l\.?)\s*$/i, '');

      // Handle negative values in parentheses
      if (cleanValue.startsWith('(') && cleanValue.endsWith(')')) {
        cleanValue = '-' + cleanValue.slice(1, -1);
      }

      // Remove commas and spaces
      cleanValue = cleanValue.replace(/[,\s]/g, '');

      // Allow empty values
      if (cleanValue === '' || cleanValue === '-') {
        return null;
      }

      // Validate numeric format
      if (!/^-?\d+(\.\d{1,2})?$/.test(cleanValue)) {
        return 'Must be a valid number with optional units (e.g., 123.64 Lacs, 5.5 Crores)';
      }
      return null;
    }

    // 4. Registration Number validation
    if (fieldLower.includes('registration')) {
      // Special case for UDYAM registration numbers - allow hyphens
      if (fieldLower.includes('udyam')) {
        // UDYAM format: UDYAM-XX-##-#######
        if (!/^[A-Z0-9-]+$/i.test(value)) {
          return 'UDYAM registration number can only contain letters, numbers, and hyphens';
        }
      } else {
        // Other registration numbers - alphanumeric only
        if (!/^[A-Z0-9]+$/i.test(value)) {
          return 'Only alphanumeric characters allowed';
        }
      }
      return null;
    }

    // 5. Amount/Numeric fields - numbers only (with optional commas and decimals)
    //  FIX: Exclude termOfFacility and tenureOfEachTranch from numeric validation (they contain text like "5 YEAR")
    if (fieldLower === 'termoffacility' || fieldLower === 'term of facility' ||
      fieldLower === 'tenureofeachtranch' || fieldLower === 'tenure of each tranch') {
      // These fields can contain text like "5 YEAR", "NULL", etc. - no validation needed
      return null;
    }

    const numericKeywords = ['amount', 'facility', 'term', 'tenure', 'income', 'tax', 'payable', 'paid', 'refundable', 'balance', 'sanctioned', 'overdue', 'score', 'accounts', 'enquiries', 'sales', 'stock', 'purchase', 'profit', 'loss', 'expense', 'revenue', 'cost', 'total', 'gross', 'net', 'months', 'days', 'years', 'count', 'number', 'limit', 'outstanding', 'delinquency', 'dpd', 'ebitda', 'depreciation', 'interest', 'pat'];
    if (numericKeywords.some(keyword => fieldLower.includes(keyword))) {
      // Handle various financial number formats
      let cleanValue = String(value).trim();

      // Handle negative values in parentheses: (1,234.56) -> -1234.56
      if (cleanValue.startsWith('(') && cleanValue.endsWith(')')) {
        cleanValue = '-' + cleanValue.slice(1, -1);
      }

      // Remove commas, spaces, and other formatting
      cleanValue = cleanValue.replace(/[,\s]/g, '');

      // Allow empty values (optional fields)
      if (cleanValue === '' || cleanValue === '-') {
        return null;
      }

      // Validate numeric format: optional minus, digits, optional decimal with up to 2 places
      if (!/^-?\d+(\.\d{1,2})?$/.test(cleanValue)) {
        return 'Must be a valid number (e.g., 1234.56, -1234.56, or (1234.56) for negative)';
      }
      return null;
    }

    // 4. Aadhaar format: 12 digits
    if (fieldLower.includes('aadhaar') || fieldLower.includes('aadhar')) {
      const cleanValue = String(value).replace(/\s/g, '');
      if (!/^\d{12}$/.test(cleanValue)) {
        return 'Aadhaar must be 12 digits';
      }
      return null;
    }

    // 5. Email format
    if (fieldLower.includes('email')) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Invalid email format';
      }
      return null;
    }

    // 6. Phone format: 10 digits
    if (fieldLower.includes('phone') || fieldLower.includes('mobile')) {
      const cleanValue = String(value).replace(/[\s-]/g, '');
      if (!/^\d{10}$/.test(cleanValue)) {
        return 'Phone must be 10 digits';
      }
      return null;
    }

    // 7. GST format: 15 alphanumeric characters (flexible validation)
    if (fieldLower.includes('gst')) {
      // Remove spaces and convert to uppercase for validation
      const cleanValue = String(value).replace(/\s/g, '').toUpperCase();

      // Basic GST validation: 15 characters, alphanumeric
      if (!/^[0-9A-Z]{15}$/i.test(cleanValue)) {
        return 'GST number must be 15 alphanumeric characters';
      }

      // Optional: More strict validation (uncomment if needed)
      // Standard GST format: 2 digits + 5 letters + 4 digits + 1 letter + 1 digit/letter + Z + 1 digit/letter
      // if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(cleanValue)) {
      //   return 'Invalid GST format (should be: 22AAAAA0000A1Z5)';
      // }

      return null;
    }

    // 8. Name fields, Type fields, and Form fields validation
    if (fieldLower.includes('name') || fieldLower.includes('owner') || fieldLower.includes('type') || (fieldLower.includes('form') && fieldLower.includes('business'))) {
      // Special case for Type Of Enterprise - allow parentheses, hyphens, and numbers
      if (fieldLower.includes('type') && fieldLower.includes('enterprise')) {
        if (!/^[A-Za-z\s.()0-9-]+$/.test(value)) {
          return 'Type of Enterprise can contain letters, spaces, dots, parentheses, hyphens, and numbers';
        }
      } else {
        // Other name/type fields - only letters, spaces, and dots
        if (!/^[A-Za-z\s.]+$/.test(value)) {
          return 'Only letters and spaces allowed';
        }
      }
      return null;
    }

    // 9. Name fields, Type fields, and Form fields validation
    if (fieldLower === 'status') {
      if (!/^[A-Za-z\s]+$/.test(value)) {
        return 'Only letters allowed';
      }
      return null;
    }

    // 10. Gender field - only letters
    if (fieldLower === 'gender') {
      if (!/^[A-Za-z]+$/.test(value)) {
        return 'Only letters allowed';
      }
      return null;
    }

    return null; // No error
  };

  //  FIX: Sync refs with props to avoid stale closure
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    saveAdapterRef.current = saveAdapter;
  }, [saveAdapter]);

  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  useEffect(() => {
    console.log(' [EXTRACT DATA] ========================================');
    console.log(' [EXTRACT DATA] useEffect triggered - data changed');

    // Create a unique identity for the current data
    // ✅ FIX: Build unique identity that distinguishes ALL document types
    // Previous code had identity collisions: GST, Company CIBIL, Individual CIBIL, Silver
    // all produced the same "APP123____" key → snapshot cross-contamination
    const _appId = data?.applicationId || data?.ApplicationID || '';
    const _roleKey = data?.roleCategory || data?.Role_Category || data?.role_category || '';
    const _docTypeKey = data?.documentType || data?.DocumentType || '';
    const _yearKey = data?.Assessment_Year || data?.assessmentYear || data?.Fiscal_Year || data?.fiscalYear || '';

    // Detect document type by presence of characteristic fields (same logic as saveField)
    const _hasGst = data && (
      Object.prototype.hasOwnProperty.call(data, 'gstin') ||
      Object.prototype.hasOwnProperty.call(data, 'GSTIN') ||
      Object.prototype.hasOwnProperty.call(data, 'LegalNameOfBusiness') ||
      Object.prototype.hasOwnProperty.call(data, 'legalNameOfBusiness')
    );
    const _hasCompanyCibil = data && (
      Object.prototype.hasOwnProperty.call(data, 'cibilMsmeScore') ||
      Object.prototype.hasOwnProperty.call(data, 'CIBIL_MSME_Score') ||
      Object.prototype.hasOwnProperty.call(data, 'nameOfCompany') ||
      Object.prototype.hasOwnProperty.call(data, 'Name_of_Company')
    );
    const _hasCibil = data && !_hasCompanyCibil && (
      Object.prototype.hasOwnProperty.call(data, 'cibilScore') ||
      Object.prototype.hasOwnProperty.call(data, 'CIBILScore') ||
      Object.prototype.hasOwnProperty.call(data, 'CIBIL_Score') ||
      Object.prototype.hasOwnProperty.call(data, 'totalAdvanceBalance') ||
      Object.prototype.hasOwnProperty.call(data, 'Total_Advance_Balance')
    );
    const _hasProfitLoss = data && (
      Object.prototype.hasOwnProperty.call(data, 'sales') ||
      Object.prototype.hasOwnProperty.call(data, 'Sales') ||
      Object.prototype.hasOwnProperty.call(data, 'ebitda') ||
      Object.prototype.hasOwnProperty.call(data, 'EBITDA')
    );
    const _hasBalanceSheet = data && !_hasProfitLoss && (
      Object.prototype.hasOwnProperty.call(data, 'capital') ||
      Object.prototype.hasOwnProperty.call(data, 'Capital') ||
      Object.prototype.hasOwnProperty.call(data, 'Short_Term_Debt') ||
      Object.prototype.hasOwnProperty.call(data, 'shortTermDebt')
    );
    const _hasItr = data && (
      Object.prototype.hasOwnProperty.call(data, 'acknowledgementNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'Acknowledgement_Number') ||
      Object.prototype.hasOwnProperty.call(data, 'formNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'Form_Number')
    );
    const _hasUdyam = data && (
      Object.prototype.hasOwnProperty.call(data, 'udyamRegistrationNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'enterpriseName') ||
      Object.prototype.hasOwnProperty.call(data, 'bankAccountNumber')
    );
    const _hasNetWorth = data && !_hasBalanceSheet && (
      Object.prototype.hasOwnProperty.call(data, 'candidateName') ||
      Object.prototype.hasOwnProperty.call(data, 'CandidateName')
    );
    const _hasSilver = data && (
      Object.prototype.hasOwnProperty.call(data, 'facilityAmount') ||
      Object.prototype.hasOwnProperty.call(data, 'FacilityAmount') ||
      Object.prototype.hasOwnProperty.call(data, 'typeOfLoan') ||
      Object.prototype.hasOwnProperty.call(data, 'TypeOfLoan') ||
      Object.prototype.hasOwnProperty.call(data, 'disbursalToName') ||
      Object.prototype.hasOwnProperty.call(data, 'DisbursalToName')
    );

    const _docTypeSegment = _hasGst ? 'gst'
      : _hasCompanyCibil ? 'company_cibil'
        : _hasCibil ? 'cibil'
          : _hasProfitLoss ? 'profit_loss'
            : _hasBalanceSheet ? 'balance_sheet'
              : _hasItr ? 'itr'
                : _hasUdyam ? 'udyam'
                  : _hasNetWorth ? 'networth'
                    : _hasSilver ? 'silver'
                      : 'doc';

    const newDataIdentity = data
      ? `${_appId}_${_docTypeSegment}_${_roleKey}_${_docTypeKey}_${_yearKey}`
      : null;
    const hasDataIdentityChanged = currentDataIdentity.current !== null && currentDataIdentity.current !== newDataIdentity;

    console.log('🟡 [EXTRACT DATA] Data Identity Check:');
    console.log('  - Previous identity:', currentDataIdentity.current);
    console.log('  - New identity:', newDataIdentity);
    console.log('  - Identity changed:', hasDataIdentityChanged);
    console.log('  - formPopulatedRef:', formPopulatedRef.current);
    console.log('  - Edited fields:', Array.from(editedFields.current));

    // ✅ FINAL FIX: null/undefined data pe kuch mat karo
    if (!data) {
      console.log('🟡 [EXTRACT DATA] ⏭️ data is null/undefined - skipping');
      return;
    }

    // ✅ FINAL FIX: Same document pe koi bhi change se formData protect karo
    if (!hasDataIdentityChanged && currentDataIdentity.current !== null) {
      // ✅ KEY FIX: Snapshot mein save karo current formData (edited values preserve honge)
      // Yeh ensure karta hai ki jab wapas is document pe aayein toh edited values milein
      if (formPopulatedRef.current) {
        console.log('🟡 [EXTRACT DATA] ✅ PROTECTED: Same document - blocking reset, snapshot preserved');
        return;
      }
    }

    // ✅ FINAL FIX: Naya document aaya — check karo snapshot mein saved data hai
    if (hasDataIdentityChanged && newDataIdentity && formDataSnapshotRef.current[newDataIdentity]) {
      console.log('🟡 [EXTRACT DATA] 🔄 Restoring snapshot for document:', newDataIdentity);
      // Save current document ka snapshot before switching
      if (currentDataIdentity.current && formPopulatedRef.current) {
        // formData snapshot save karna hai - but formData state stale ho sakta hai
        // isliye hum setFormData use karenge snapshot se
      }
      currentDataIdentity.current = newDataIdentity;
      formPopulatedRef.current = true;
      editedFields.current.clear();
      setFormData(formDataSnapshotRef.current[newDataIdentity]);
      return;
    }

    // 🔥 CRITICAL FIX: NEVER update formData if we have ANY edited fields
    const hasUnsavedEdits = editedFields.current.size > 0;

    // If we have unsaved edits, ALWAYS preserve local state
    if (hasUnsavedEdits) {
      console.log('🟡 [EXTRACT DATA] 🛑 BLOCKED UPDATE - Has unsaved edits:', Array.from(editedFields.current));
      return;
    }

    // If we reach here, it's safe to update (new document or initial load)
    console.log('🟡 [EXTRACT DATA] Updating formData with fresh data for:', newDataIdentity);

    // Update the current identity
    currentDataIdentity.current = newDataIdentity;

    // Clear edited fields when switching documents
    editedFields.current.clear();
    formPopulatedRef.current = false; // will be set to true after setFormData below

    // Helper function to safely process values with date formatting
    const safeProcessValue = (value) => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return '[Object]';
        }
      }
      // ✅ FIX: ISO date format (2025-09-28T00:00:00.000Z) → DD/MM/YYYY
      const strVal = String(value);
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(strVal)) {
        try {
          const d = new Date(strVal);
          if (!isNaN(d.getTime())) {
            const day = String(d.getUTCDate()).padStart(2, '0');
            const month = String(d.getUTCMonth() + 1).padStart(2, '0');
            const year = d.getUTCFullYear();
            return `${day}/${month}/${year}`;
          }
        } catch { /* fallthrough */ }
      }
      return strVal;
    };

    const safeProcessObject = (obj) => {
      const processedObj = {};
      for (const [key, value] of Object.entries(obj)) {
        if (isDateFieldUtil(key)) {
          processedObj[key] = formatDateToDDMMYYYY(value) || value || '';
        } else {
          processedObj[key] = safeProcessValue(value);
        }
      }
      return processedObj;
    };

    if (data && typeof data === 'object') {
      const exclude = new Set([
        'id', 'applicationId', 'loanAppFormId', 'pdfPath', 'pdfs', 'jsonPath',
        'assessmentYear', 'companyId', 'individualId', 'fileId', 'verificationId',
        'roleCategory', 'natureOfBusiness', 'createdBy', 'created_by', 'createdAt',
        'created_at', 'updatedBy', 'updated_by', 'updatedAt', 'updated_at',
        'Created_By', 'Created_At', 'Updated_By', 'Updated_At', 'gender', 'address',
        'aadhaarType', 'documentType', 'JsonPath', 'PdfPath', 'Individual_ID',
        'Company_ID', 'File_ID', 'Verification_ID', 'Role_Category', 'role_category',
        'DocumentType', 'Gender', 'Address', 'AadhaarType', 'ApplicationID', 'ID',
        'Assessment_Year', 'AssessmentYear', 'Fiscal_Year', 'FiscalYear', 'udyamId',
        'UdyamID', 'Udyam_ID', 'udyam_id', 'gstId', 'GST_ID', 'Gst_ID', 'gst_id',
        'Proprietorship', 'Proprietor', 'Co Applicant', 'proprietorship', 'proprietor',
        'co applicant', 'scores', 'score', 'verificationProgress', 'verification_progress',
        'fileProgress', 'file_progress', 'verification', 'documents', 'coApplicant',
        'co_applicant', 'Co_Applicant', 'CoApplicant', 'cibilScore', 'bankConnectScore',
        'cibilMsmeScore', 'totalScore', 'overallScore', 'progress', 'status',
        'fileStatus', 'verificationStatus', 'documentStatus',
        // ✅ Silver loan app - complex JSON objects (hide from manual review UI)
        'loanDetails', 'LoanDetails', 'loan_details',
        'businessInfo', 'BusinessInfo', 'business_info',
        'applicants', 'Applicants',
        'metadata', 'Metadata',
        'memoNo', 'MemoNo', 'memo_no',
        'azureStorage', 'AzureStorage', 'azure_storage',
        'registrationDate', 'RegistrationDate', 'registration_date',
        // ✅ Confidence scores - exclude from form display (shown as badges instead)
        'confidenceScores', 'ConfidenceScores', 'confidence_scores',
      ]);

      // [Keep all your section detection logic - CIBIL, NetWorth, Aadhaar, PAN, etc.]
      // Just paste your existing section detection code here...
      // I'm not including it to keep this focused on the fix

      // For now, use default behavior
      const entries = Object.entries(data)
        .filter(([k]) => !exclude.has(k))
        .map(([k, v]) => [k, v ?? ""]);

      const processedEntries = entries.map(([k, v]) => [k, safeProcessValue(v)]);
      const newFormData = Object.fromEntries(processedEntries);
      setFormData(newFormData);
      formPopulatedRef.current = true; // ✅ Mark form as populated
      // ✅ KEY FIX: Initial load ka snapshot save karo
      if (newDataIdentity) {
        formDataSnapshotRef.current[newDataIdentity] = newFormData;
        console.log('🟡 [EXTRACT DATA] ✅ Snapshot saved for:', newDataIdentity);
      }
    } else {
      setFormData({});
      formPopulatedRef.current = false; // ✅ Reset when no data
    }
  }, [data]); // Only depend on data prop
  useEffect(() => {
    if (editingField && inputRefs.current[editingField]) {
      const input = inputRefs.current[editingField];
      // Use setTimeout to ensure the TextField is fully rendered
      setTimeout(() => {
        if (input) {
          input.focus();
          // Set cursor to the end of the text
          const length = input.value?.length || 0;
          input.setSelectionRange(length, length);
        }
      }, 0);
    }
  }, [editingField]);

  const handleEdit = useCallback((field) => {
    if (editingField === field) {
      setEditingField(null);
      setHighlightField(null);
    } else {
      setEditingField(field);
      setHighlightField(field);
    }
  }, [editingField]);

  const handleChange = useCallback((field, value) => {
    console.log(`🔵 [HANDLE CHANGE] ========================================`);
    console.log(`🔵 [HANDLE CHANGE] Field: ${field}`);
    console.log(`🔵 [HANDLE CHANGE] New Value: "${value}"`);

    // 🔥 CRITICAL: Mark as edited FIRST (before ANY async operations)
    editedFields.current.add(field);
    console.log(`🔵 [HANDLE CHANGE] ✅ Marked as edited:`, field);

    // Update state immediately
    setFormData((prev) => {
      const newState = { ...prev, [field]: value };
      console.log(`🔵 [HANDLE CHANGE] Updated state for ${field}`);
      return newState;
    });

    // Validate
    const error = validateField(field, value);

    // Update errors
    setErrors(prev => {
      if (error) {
        return { ...prev, [field]: error };
      } else {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
    });

    if (!error) {
      console.log(`🔵 [HANDLE CHANGE] Valid - saving...`);
      // Save immediately (no setTimeout)
      saveField(field, value).then(() => {
        // Only clear after save completes
        editedFields.current.delete(field);
        console.log(`🔵 [HANDLE CHANGE] ✅ Saved & cleared:`, field);
      }).catch((err) => {
        console.error(`🔵 [HANDLE CHANGE] ❌ Save failed:`, err);
        // Keep marked as edited if save fails
      });
    } else {
      console.warn(` [IMMEDIATE SAVE] ❌ Validation error for ${field}: ${error}`);
    }
  }, []);

  const handleKeyPress = useCallback(async (event, field) => {
    console.log(` [HANDLE KEY PRESS] Key: ${event.key}, Field: ${field}`);
    if (event.key === "Enter") {
      console.log(` [HANDLE KEY PRESS] Enter pressed - saving field ${field}`);
      // Get current value from formData state
      setFormData((prev) => {
        // Defer saveField call to avoid initialization error
        setTimeout(() => saveField(field, prev[field]), 0);
        return prev;
      });
      setEditingField(null);
      setHighlightField(null);
    }
  }, []);

  const handleBlur = useCallback(async (field) => {
    console.log(` [HANDLE BLUR] Field: ${field} - exiting edit mode`);

    // 🔥 FIX: Don't exit edit mode if we're clicking the edit button
    if (editButtonClickRef.current) {
      editButtonClickRef.current = false;
      return;
    }

    // Just exit editing mode, no need to save (already saved on change)
    setEditingField(null);
    setHighlightField(null);

    // Clear any validation errors when field loses focus
    setErrors(prevErrors => ({ ...prevErrors, [field]: null }));

    // Show toast if this field was successfully saved
    if (lastSavedRef.current[field] !== undefined) {
      const savedValue = lastSavedRef.current[field];
      delete lastSavedRef.current[field]; // clear so it doesn't show again
      setSavedToast({ field, label: fieldLabels[field] || formatLabel(field), value: savedValue });
      setTimeout(() => setSavedToast(null), 3000);
    }
  }, []);

  // Check if a field is a date field
  const isDateField = useCallback((field) => {
    return isDateFieldUtil(field);
  }, []);

  // Handle date change from DatePicker
  const handleDateChange = useCallback(async (field, newValue) => {
    console.log(` [HANDLE DATE CHANGE] Field: ${field}, New Value: ${newValue}`);
    if (newValue && dayjs(newValue).isValid()) {
      const formattedDate = dayjs(newValue).format('DD/MM/YYYY');
      console.log(` [HANDLE DATE CHANGE] Formatted date: ${formattedDate}`);
      setFormData((prev) => ({ ...prev, [field]: formattedDate }));
      // Defer saveField call to avoid initialization error
      setTimeout(() => {
        saveField(field, formattedDate).then(() => {
          // Show toast after date field is saved (date fields don't go through handleBlur)
          setSavedToast({ field, label: fieldLabels[field] || formatLabel(field), value: formattedDate });
          setTimeout(() => setSavedToast(null), 3000);
        }).catch(() => { });
      }, 0);
    }
    setDatePickerOpen(null);
    setHighlightField(null);
  }, []);

  // 🔥 NOTE: Field handlers are created inline in the map function for immediate availability
  // This ensures handlers are always fresh and tied to the correct field

  // 🔥 FIX: Memoized date picker close handler
  const datePickerCloseHandler = useCallback(() => {
    setDatePickerOpen(null);
    setHighlightField(null);
  }, []);
  const formatDateIfNeeded = (value) => {
    if (!value || typeof value !== "string") return value;

    // ISO with or without milliseconds
    const isoRegex = /^\d{4}-\d{2}-\d{2}T/;
    if (isoRegex.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          const year = date.getUTCFullYear();
          return `${month}/${day}/${year}`;
        }
      } catch {
        return value;
      }
    }

    // Month name format
    const monthNameRegex =
      /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}\s+\d{4}/i;

    if (monthNameRegex.test(value)) {
      const parts = value.replace(/PM|AM/i, "").trim().split(/\s+/);
      const monthNames = {
        Jan: "01", Feb: "02", Mar: "03", Apr: "04",
        May: "05", Jun: "06", Jul: "07", Aug: "08",
        Sep: "09", Oct: "10", Nov: "11", Dec: "12",
      };

      const month = monthNames[parts[0].substring(0, 3)];
      const day = parts[1].padStart(2, "0");
      const year = parts[2];

      return `${month}/${day}/${year}`;
    }

    // YYYY-MM-DD
    const numeric1 = value.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})/);
    if (numeric1) {
      const [, year, month, day] = numeric1;
      return `${month}/${day}/${year}`;
    }

    // DD-MM-YYYY
    const numeric2 = value.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})/);
    if (numeric2) {
      const [, day, month, year] = numeric2;
      return `${month}/${day}/${year}`;
    }

    return value;
  };


  const saveField = async (field, value) => {
    console.log('🟢 [SAVE FIELD] ========================================');
    console.log('🟢 [SAVE FIELD] Starting save process...');
    console.log('🟢 [SAVE FIELD] Field:', field);
    console.log('🟢 [SAVE FIELD] Value to save:', value);
    console.log('🟢 [SAVE FIELD] Value type:', typeof value);

    // 🔥 FIX: Use refs to avoid stale closure
    const data = dataRef.current;
    const saveAdapter = saveAdapterRef.current;
    const onSaved = onSavedRef.current;

    // Debug: Check data structure and find applicationId
    console.log('🟢 [SAVE FIELD] Data object:', data);
    console.log('🟢 [SAVE FIELD] Data keys:', data ? Object.keys(data) : 'no data');

    // Try multiple possible field names for applicationId (prioritize the correct one)
    const appId = data?.ApplicationID || data?.applicationId || data?.application_id || data?.appId || data?.id;
    console.log('🟢 [SAVE FIELD] AppId candidates:');
    console.log('  - data.ApplicationID:', data?.ApplicationID);
    console.log('  - data.applicationId:', data?.applicationId);
    console.log('  - data.application_id:', data?.application_id);
    console.log('  - data.appId:', data?.appId);
    console.log('  - data.id:', data?.id);
    console.log('🟢 [SAVE FIELD] Final AppId:', appId);

    if (!appId) {
      console.warn('🟢 [SAVE FIELD] No applicationId found in data object');
      console.warn('🟢 [SAVE FIELD] Available data keys:', data ? Object.keys(data) : 'no data');
      return;
    }

    // Detect section type by presence of characteristic keys
    // Order matters: more specific checks first
    const isItr = data && (
      Object.prototype.hasOwnProperty.call(data, 'acknowledgementNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'Acknowledgement_Number') ||  // Database field name
      Object.prototype.hasOwnProperty.call(data, 'formNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'Form_Number') ||             // Database field name
      Object.prototype.hasOwnProperty.call(data, 'totalIncome') ||
      Object.prototype.hasOwnProperty.call(data, 'Total_Income')               // Database field name
    );

    const isAadhaar = data && !isItr && (  // Only if NOT ITR
      Object.prototype.hasOwnProperty.call(data, 'aadhaarNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'AadhaarNumber') ||  // Also check capital A version
      Object.prototype.hasOwnProperty.call(data, 'aadhaarType') ||
      Object.prototype.hasOwnProperty.call(data, 'AadhaarType') ||    // Also check capital A version
      (typeof data.documentType === 'string' && data.documentType.trim().toLowerCase() === 'aadhaar') ||
      (typeof data.DocumentType === 'string' && data.DocumentType.trim().toLowerCase() === 'aadhaar')  // Also check capital D version
    );

    // Check Company CIBIL BEFORE PAN (since Company CIBIL also has panNumber)
    const isCompanyCibil = data && (
      Object.prototype.hasOwnProperty.call(data, 'cibilMsmeScore') ||
      Object.prototype.hasOwnProperty.call(data, 'CIBIL_MSME_Score') ||  // Database field name
      Object.prototype.hasOwnProperty.call(data, 'nameOfCompany') ||
      Object.prototype.hasOwnProperty.call(data, 'Name_of_Company') ||  // Database field name
      Object.prototype.hasOwnProperty.call(data, 'totalOutstandingAmount') ||
      Object.prototype.hasOwnProperty.call(data, 'Total_Outstanding_Amount')  // Database field name
    );

    // PAN detection - only if NOT Company CIBIL
    const isPan = data && !isCompanyCibil && (
      (typeof data.documentType === 'string' && data.documentType.trim().toLowerCase() === 'pan') ||
      (typeof data.DocumentType === 'string' && data.DocumentType.trim().toLowerCase() === 'pan') ||  // Capital D version
      Object.prototype.hasOwnProperty.call(data, 'panNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'PANNumber')     // Capital version
    );
    const isNetWorth = data && (
      Object.prototype.hasOwnProperty.call(data, 'netWorth') ||
      Object.prototype.hasOwnProperty.call(data, 'totalLiabilities') ||
      Object.prototype.hasOwnProperty.call(data, 'candidateName')
    );
    const isCibil = data && (
      Object.prototype.hasOwnProperty.call(data, 'cibilScore') ||
      Object.prototype.hasOwnProperty.call(data, 'CIBILScore') ||  // PascalCase
      Object.prototype.hasOwnProperty.call(data, 'CIBIL_Score') ||  // Database field name
      Object.prototype.hasOwnProperty.call(data, 'totalAdvanceBalance') ||
      Object.prototype.hasOwnProperty.call(data, 'TotalAdvanceBalance') ||  // PascalCase
      Object.prototype.hasOwnProperty.call(data, 'Total_Advance_Balance')  // Database field name
    );
    const isBalanceSheet = data && (
      Object.prototype.hasOwnProperty.call(data, 'netWorth') ||
      Object.prototype.hasOwnProperty.call(data, 'totalAssets') ||
      Object.prototype.hasOwnProperty.call(data, 'capital')
    );
    const isProfitLoss = data && (
      Object.prototype.hasOwnProperty.call(data, 'sales') ||
      Object.prototype.hasOwnProperty.call(data, 'Sales') ||           // Capital S
      Object.prototype.hasOwnProperty.call(data, 'ebitda') ||
      Object.prototype.hasOwnProperty.call(data, 'EBITDA') ||          // All caps
      Object.prototype.hasOwnProperty.call(data, 'fiscalYear') ||
      Object.prototype.hasOwnProperty.call(data, 'Fiscal_Year')        // Database format
    );
    const isGst = data && (
      Object.prototype.hasOwnProperty.call(data, 'gstin') ||
      Object.prototype.hasOwnProperty.call(data, 'GSTIN') ||  // Database field name
      Object.prototype.hasOwnProperty.call(data, 'legalNameOfBusiness') ||
      Object.prototype.hasOwnProperty.call(data, 'LegalNameOfBusiness') ||  // Database field name
      Object.prototype.hasOwnProperty.call(data, 'tradeName') ||
      Object.prototype.hasOwnProperty.call(data, 'TradeName')  // Database field name
    );
    const isUdyam = data && (
      Object.prototype.hasOwnProperty.call(data, 'udyamRegistrationNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'enterpriseName') ||
      Object.prototype.hasOwnProperty.call(data, 'bankAccountNumber')
    );
    const isBankStat = data && (
      Object.prototype.hasOwnProperty.call(data, 'totalNoOfCreditTransactions') ||
      Object.prototype.hasOwnProperty.call(data, 'totalNoOfDebitTransactions') ||
      Object.prototype.hasOwnProperty.call(data, 'averageEODBalance')
    );
    const isBankStatSummary = data && (
      Object.prototype.hasOwnProperty.call(data, 'accountHolderName') ||
      Object.prototype.hasOwnProperty.call(data, 'accountNumber') ||
      Object.prototype.hasOwnProperty.call(data, 'bankConnectScore')
    );

    console.log(' [SAVE FIELD] Section Detection:');
    console.log('  - isUdyam:', isUdyam);
    console.log('  - isAadhaar:', isAadhaar);
    console.log('  - isPan:', isPan);
    console.log('  - isItr:', isItr);
    console.log('  - isGst:', isGst);
    console.log('  - isNetWorth:', isNetWorth);
    console.log('  - Data keys:', data ? Object.keys(data) : 'no data');

    // Additional Aadhaar debugging
    if (isAadhaar) {
      console.log(' [AADHAAR DEBUG] Aadhaar section detected!');
      console.log('  - Has aadhaarNumber:', Object.prototype.hasOwnProperty.call(data, 'aadhaarNumber'));
      console.log('  - Has AadhaarNumber:', Object.prototype.hasOwnProperty.call(data, 'AadhaarNumber'));
      console.log('  - Has name:', Object.prototype.hasOwnProperty.call(data, 'name'));
      console.log('  - Has Name:', Object.prototype.hasOwnProperty.call(data, 'Name'));
      console.log('  - Has dob:', Object.prototype.hasOwnProperty.call(data, 'dob'));
      console.log('  - Has DOB:', Object.prototype.hasOwnProperty.call(data, 'DOB'));
      console.log('  - documentType:', data.documentType);
      console.log('  - DocumentType:', data.DocumentType);
    }

    // Additional PAN debugging
    if (isPan) {
      console.log(' [PAN DEBUG] PAN section detected!');
      console.log('  - Has panNumber:', Object.prototype.hasOwnProperty.call(data, 'panNumber'));
      console.log('  - Has PANNumber:', Object.prototype.hasOwnProperty.call(data, 'PANNumber'));
      console.log('  - Has name:', Object.prototype.hasOwnProperty.call(data, 'name'));
      console.log('  - Has Name:', Object.prototype.hasOwnProperty.call(data, 'Name'));
      console.log('  - Has dob:', Object.prototype.hasOwnProperty.call(data, 'dob'));
      console.log('  - Has DOB:', Object.prototype.hasOwnProperty.call(data, 'DOB'));
      console.log('  - documentType:', data.documentType);
      console.log('  - DocumentType:', data.DocumentType);
    }

    // Additional ITR debugging
    if (isItr) {
      console.log(' [ITR DEBUG] ITR section detected!');
      console.log('  - Has acknowledgementNumber:', Object.prototype.hasOwnProperty.call(data, 'acknowledgementNumber'));
      console.log('  - Has Acknowledgement_Number:', Object.prototype.hasOwnProperty.call(data, 'Acknowledgement_Number'));
      console.log('  - Has formNumber:', Object.prototype.hasOwnProperty.call(data, 'formNumber'));
      console.log('  - Has Form_Number:', Object.prototype.hasOwnProperty.call(data, 'Form_Number'));
      console.log('  - Has totalIncome:', Object.prototype.hasOwnProperty.call(data, 'totalIncome'));
      console.log('  - Has Total_Income:', Object.prototype.hasOwnProperty.call(data, 'Total_Income'));
    }

    // Additional NetWorth debugging
    if (isNetWorth) {
      console.log(' [NETWORTH DEBUG] NetWorth section detected!');
      console.log('  - Has totalAssets:', Object.prototype.hasOwnProperty.call(data, 'totalAssets'));
      console.log('  - Has TotalAssets:', Object.prototype.hasOwnProperty.call(data, 'TotalAssets'));
      console.log('  - Has totalLiabilities:', Object.prototype.hasOwnProperty.call(data, 'totalLiabilities'));
      console.log('  - Has TotalLiabilities:', Object.prototype.hasOwnProperty.call(data, 'TotalLiabilities'));
      console.log('  - Has netWorth:', Object.prototype.hasOwnProperty.call(data, 'netWorth'));
      console.log('  - Has NetWorth:', Object.prototype.hasOwnProperty.call(data, 'NetWorth'));
    }

    // Build mapping per section
    const silverMap = {
      // Application meta
      loanApplicationRegistrationDate: 'LoanApplicationRegistrationDate',
      'Registration Date': 'LoanApplicationRegistrationDate',  // 🔥 FIX: Add display name mapping
      loanApplicationRegistrationNo: 'LoanApplicationRegistrationNo',
      typeOfLoan: 'TypeOfLoan',
      formOfBusiness: 'FormOfBusiness',
      'Form Of Business': 'FormOfBusiness',  // 🔥 FIX: Add display name mapping
      natureOfBusiness: 'NatureOfBusiness',
      facilityAmount: 'FacilityAmount',
      'Loan Amount': 'FacilityAmount',  // 🔥 FIX: Add display name mapping
      termOfFacility: 'TermOfFacility',
      'Term Of Facility': 'TermOfFacility',  // 🔥 FIX: Add display name mapping
      tenureOfEachTranch: 'TenureOfEachTranch',
      'Tenure Of Each Tranch': 'TenureOfEachTranch',  // 🔥 FIX: Add display name mapping
      disbursalToName: 'DisbursalToName',
      'Disbursal To Name': 'DisbursalToName',  // 🔥 FIX: Add display name mapping
      disbursalToAccount: 'DisbursalToAccount',
      'Disbursal To Account': 'DisbursalToAccount',  // 🔥 FIX: Add display name mapping
      // Co-Applicant
      nameOfCoApplicant: 'NameOfCoApplicant',
      'Co-Applicant Name': 'NameOfCoApplicant',  // 🔥 FIX: Add display name mapping
      panCoApplicant: 'PANCoApplicant',
      'Co-Applicant PAN': 'PANCoApplicant',  // 🔥 FIX: Add display name mapping
      aadhaarCoApplicant: 'AadhaarCoApplicant',
      'Co-Applicant Aadhaar': 'AadhaarCoApplicant',  // 🔥 FIX: Add display name mapping
      addressCoApplicant: 'AddressCoApplicant',
      'Co-Applicant Address': 'AddressCoApplicant',  // 🔥 FIX: Add display name mapping
      emailCoApplicant: 'EmailCoApplicant',
      'Co-Applicant Email': 'EmailCoApplicant',  // 🔥 FIX: Add display name mapping
      phoneCoApplicant: 'PhoneCoApplicant',
      'Co-Applicant Phone': 'PhoneCoApplicant',  // 🔥 FIX: Add display name mapping
      // Company fields (Proprietorship)
      'Company Name': 'NameOfBorrowerProprietorship',  // 🔥 FIX: Add display name mapping
      'Company PAN': 'PANProprietorship',  // 🔥 FIX: Add display name mapping
      'Company GST': 'GSTProprietorship',  // 🔥 FIX: Add display name mapping
      'Company Aadhaar': 'AadhaarProprietorship',  // 🔥 FIX: Add display name mapping
      'Company Address': 'AddressProprietorship',  // 🔥 FIX: Add display name mapping
      'Company Email': 'EmailProprietorship',  // 🔥 FIX: Add display name mapping
      'Company Phone': 'PhoneProprietorship',  // 🔥 FIX: Add display name mapping
      // Proprietor
      nameOfBorrowerProprietor: 'NameOfBorrowerProprietor',
      'Proprietor Name': 'NameOfBorrowerProprietor',  // 🔥 FIX: Add display name mapping
      panProprietor: 'PANProprietor',
      'Proprietor PAN': 'PANProprietor',  // 🔥 FIX: Add display name mapping
      aadhaarProprietor: 'AadhaarProprietor',
      'Proprietor Aadhaar': 'AadhaarProprietor',  // 🔥 FIX: Add display name mapping
      addressProprietor: 'AddressProprietor',
      'Proprietor Address': 'AddressProprietor',  // 🔥 FIX: Add display name mapping
      emailProprietor: 'EmailProprietor',
      'Proprietor Email': 'EmailProprietor',  // 🔥 FIX: Add display name mapping
      phoneProprietor: 'PhoneProprietor',
      'Proprietor Phone': 'PhoneProprietor',  // 🔥 FIX: Add display name mapping
      // Misc
      jsonPath: 'JsonPath',
      pdfPath: 'PdfPath'
    };

    const aadhaarMap = {
      roleCategory: 'Role_Category',
      documentType: 'DocumentType',
      DocumentType: 'DocumentType',                 // Capital D version

      aadhaarNumber: 'AadhaarNumber',
      AadhaarNumber: 'AadhaarNumber',               // Capital A version (database field name as key)

      name: 'Name',
      Name: 'Name',                                 // Capital N version (database field name as key)

      dob: 'DOB',
      DOB: 'DOB',                                   // Capital version (database field name as key)

      gender: 'Gender',
      Gender: 'Gender',                             // Capital G version (database field name as key)

      address: 'Address',
      Address: 'Address',                           // Capital A version (database field name as key)

      aadhaarType: 'AadhaarType',
      AadhaarType: 'AadhaarType',                   // Capital A version (database field name as key)

      jsonPath: 'JsonPath',
      JsonPath: 'JsonPath',                         // Capital J version (database field name as key)
      pdfPath: 'PdfPath',
      PdfPath: 'PdfPath',                           // Capital P version (database field name as key)
      individualId: 'Individual_ID',
      Individual_ID: 'Individual_ID',               // Database field name as key
      companyId: 'Company_ID',
      Company_ID: 'Company_ID',                     // Database field name as key
      createdBy: 'created_by',
      created_by: 'created_by',                     // Database field name as key
      fileId: 'File_ID',
      File_ID: 'File_ID',                           // Database field name as key
      verificationId: 'Verification_ID',
      Verification_ID: 'Verification_ID'            // Database field name as key
    };

    const panMap = {
      roleCategory: 'role_category',
      role_category: 'role_category',                   // Database field name as key

      documentType: 'DocumentType',
      DocumentType: 'DocumentType',                     // Capital D version (database field name as key)

      panNumber: 'PANNumber',
      PANNumber: 'PANNumber',                           // Capital version (database field name as key)

      name: 'Name',
      Name: 'Name',                                     // Capital N version (database field name as key)

      dob: 'DOB',
      DOB: 'DOB',                                       // Capital version (database field name as key)

      jsonPath: 'JsonPath',
      JsonPath: 'JsonPath',                             // Capital J version (database field name as key)
      pdfPath: 'PdfPath',
      PdfPath: 'PdfPath',                               // Capital P version (database field name as key)
      individualId: 'Individual_ID',
      Individual_ID: 'Individual_ID',                   // Database field name as key
      companyId: 'Company_ID',
      Company_ID: 'Company_ID',                         // Database field name as key
      createdBy: 'created_by',
      created_by: 'created_by',                         // Database field name as key
      fileId: 'File_ID',
      File_ID: 'File_ID',                               // Database field name as key
      verificationId: 'Verification_ID',
      Verification_ID: 'Verification_ID'                // Database field name as key
    };

    const itrMap = {
      roleCategory: 'Role_Category',
      Role_Category: 'Role_Category',                   // Database field name as key

      assessmentYear: 'Assessment_Year',
      Assessment_Year: 'Assessment_Year',               // Database field name as key

      acknowledgementNumber: 'Acknowledgement_Number',
      Acknowledgement_Number: 'Acknowledgement_Number', // Database field name as key

      dateOfFiling: 'Date_of_Filing',
      Date_of_Filing: 'Date_of_Filing',                 // Database field name as key

      formNumber: 'Form_Number',
      Form_Number: 'Form_Number',                       // Database field name as key

      name: 'Name',
      Name: 'Name',                                     // Database field name as key

      address: 'Address',
      Address: 'Address',                               // Database field name as key

      pan: 'PAN',
      PAN: 'PAN',                                       // Database field name as key

      status: 'Status',
      Status: 'Status',                                 // Database field name as key

      totalIncome: 'Total_Income',
      Total_Income: 'Total_Income',                     // Database field name as key

      netTaxPayable: 'Net_Tax_Payable',
      Net_Tax_Payable: 'Net_Tax_Payable',               // Database field name as key

      interestAndFeePayable: 'Interest_and_Fee_Payable',
      Interest_and_Fee_Payable: 'Interest_and_Fee_Payable', // Database field name as key

      totalTaxInterestFeePayable: 'Total_Tax_Interest_Fee_Payable',
      Total_Tax_Interest_Fee_Payable: 'Total_Tax_Interest_Fee_Payable', // Database field name as key

      taxesPaid: 'Taxes_Paid',
      Taxes_Paid: 'Taxes_Paid',                         // Database field name as key

      refundableAmount: 'Refundable_Amount',
      Refundable_Amount: 'Refundable_Amount',           // Database field name as key

      jsonPath: 'JsonPath',
      JsonPath: 'JsonPath',                             // Database field name as key
      pdfPath: 'PdfPath',
      PdfPath: 'PdfPath',                               // Database field name as key
      companyId: 'Company_ID',
      Company_ID: 'Company_ID',                         // Database field name as key
      individualId: 'Individual_ID',
      Individual_ID: 'Individual_ID',                   // Database field name as key
      createdBy: 'created_by',
      created_by: 'created_by',                         // Database field name as key
      fileId: 'File_ID',
      File_ID: 'File_ID',                               // Database field name as key
      verificationId: 'Verification_ID',
      Verification_ID: 'Verification_ID'                // Database field name as key
    };

    const netWorthMap = {
      roleCategory: 'role_category',
      candidateName: 'CandidateName',
      // name removed from UI order; mapping retained only if needed server-side
      name: 'Name',
      totalAssets: 'TotalAssets',
      totalLiabilities: 'TotalLiabilities',
      netWorth: 'NetWorth',
      jsonPath: 'JsonPath',
      pdfPath: 'PdfPath',
      companyId: 'Company_ID',
      individualId: 'Individual_ID',
      fileId: 'File_ID',
      verificationId: 'Verification_ID'
    };

    const cibilMap = {
      roleCategory: 'role_category',
      role_category: 'role_category',               // Database field name as key

      ownerName: 'OwnerName',
      OwnerName: 'OwnerName',                       // Database field name as key

      cibilScore: 'CIBILScore',                     // Fixed: was CIBILScore
      CIBILScore: 'CIBILScore',                     // Database field name as key

      jsonPath: 'JsonPath',
      JsonPath: 'JsonPath',                         // Database field name as key
      pdfPath: 'PdfPath',
      PdfPath: 'PdfPath',                           // Database field name as key

      individualId: 'Individual_ID',
      Individual_ID: 'Individual_ID',               // Database field name as key
      companyId: 'Company_ID',
      Company_ID: 'Company_ID',                     // Database field name as key
      createdBy: 'created_by',
      created_by: 'created_by',                     // Database field name as key

      memberId: 'Member_ID',
      Member_ID: 'Member_ID',                       // Database field name as key
      reportDate: 'Report_Date',
      Report_Date: 'Report_Date',                   // Database field name as key
      reportTime: 'Report_Time',
      Report_Time: 'Report_Time',                   // Database field name as key
      reportDetail: 'Report_Detail',
      Report_Detail: 'Report_Detail',               // Database field name as key

      // name: 'Name',                              // Removed: doesn't exist in database
      // Name: 'Name',                               // Removed: doesn't exist in database
      gender: 'Gender',
      Gender: 'Gender',                             // Database field name as key
      dob: 'DOB',
      DOB: 'DOB',                                   // Database field name as key
      panNumber: 'PAN_Number',
      PAN_Number: 'PAN_Number',                     // Database field name as key

      ckycNumber: 'CKYC_Number',
      CKYC_Number: 'CKYC_Number',                   // Database field name as key
      kycNumber: 'CKYC_Number',                     // Backward compatibility
      KYC_Number: 'CKYC_Number',                    // Backward compatibility

      totalAccounts: 'Total_Accounts',
      Total_Accounts: 'Total_Accounts',             // Database field name as key
      totalAdvanceBalance: 'Total_Advance_Balance',
      Total_Advance_Balance: 'Total_Advance_Balance', // Database field name as key
      totalSanctionedAmount: 'Total_Sanctioned_Amount',
      Total_Sanctioned_Amount: 'Total_Sanctioned_Amount', // Database field name as key
      overdueAmount: 'Overdue_Amount',
      Overdue_Amount: 'Overdue_Amount',             // Database field name as key
      oldestOpenDate: 'Oldest_Open_Date',
      Oldest_Open_Date: 'Oldest_Open_Date',         // Database field name as key
      recentOpenDate: 'Recent_Open_Date',
      Recent_Open_Date: 'Recent_Open_Date',         // Database field name as key
      zeroBalanceAccounts: 'Zero_Balance_Accounts',
      Zero_Balance_Accounts: 'Zero_Balance_Accounts', // Database field name as key
      totalEnquiries: 'Total_Enquiries',
      Total_Enquiries: 'Total_Enquiries',           // Database field name as key
      past30Days: 'Past_30_Days',
      Past_30_Days: 'Past_30_Days',                 // Database field name as key
      past12Months: 'Past_12_Months',
      Past_12_Months: 'Past_12_Months',             // Database field name as key
      past24Months: 'Past_24_Months',
      Past_24_Months: 'Past_24_Months',             // Database field name as key
      mostRecentEnquiryDate: 'Most_Recent_Enquiry_Date',
      Most_Recent_Enquiry_Date: 'Most_Recent_Enquiry_Date', // Database field name as key
      fileId: 'File_ID',
      File_ID: 'File_ID',                           // Database field name as key
      verificationId: 'Verification_ID',
      Verification_ID: 'Verification_ID'            // Database field name as key
    };

    const companyCibilMap = {
      nameOfCompany: 'Name_of_Company',
      cibilMsmeScore: 'CIBIL_MSME_Score',
      formOfBusiness: 'Form_of_Business',
      panNumber: 'PAN_Number',
      monthsSinceOldestTradeOpened: 'Months_since_oldest_trade_opened',
      monthsSinceMostRecentDelinquency30PlusDpd: 'Months_since_most_recent_delinquency_30plus_dpd',
      maxUtilisationRevolvingTradesLast12Months: 'Max_utilisation_revolving_trades_last_12_months',
      avgUtilisationRevolvingTradesLast12Months: 'Avg_utilisation_revolving_trades_last_12_months',
      numMonthsCcOdOverUtilised: 'Num_months_CC_OD_over_utilised',
      numTimesWcLoanUtilisationGt75: 'Num_times_WC_loan_utilisation_gt75',
      numMonthsPast12mFinTradeBalanceDecreased: 'Num_months_past12m_fin_trade_balance_decreased',
      missedPaymentRatioTermloanLast12m: 'Missed_payment_ratio_termloan_last12m',
      missedPaymentRatioFinancialLast12m: 'Missed_payment_ratio_financial_last12m',
      balanceBuildupRevolvingTrades: 'Balance_buildup_revolving_trades',
      totalOutstandingAmount: 'Total_Outstanding_Amount',
      totalEnquiries: 'Total_Enquiries',
      createdAt: 'CreatedAt',
      updatedAt: 'UpdatedAt',
      jsonPath: 'JsonPath',
      pdfPath: 'PdfPath',
      fileId: 'File_ID',
      verificationId: 'Verification_ID'
    };

    const profitLossMap = {
      assessmentYear: 'Assessment_Year',
      Assessment_Year: 'Assessment_Year',

      // Map to actual database column names (which the backend now accepts)
      sales: 'Sales',
      Sales: 'Sales',                            // Database Sales -> Backend Sales

      closingStock: 'Closing_Stock',
      Closing_Stock: 'Closing_Stock',

      totalIncome: 'Total_Income',
      Total_Income: 'Total_Income',

      openingStock: 'Opening_Stock',
      Opening_Stock: 'Opening_Stock',

      purchases: 'Purchases',
      Purchases: 'Purchases',

      indirectExpenses: 'Indirect_Expenses',
      Indirect_Expenses: 'Indirect_Expenses',

      totalExpenditure: 'Total_Expenditure',
      Total_Expenditure: 'Total_Expenditure',

      ebitda: 'EBITDA',
      EBITDA: 'EBITDA',

      depreciation: 'Depreciation',
      Depreciation: 'Depreciation',

      interest: 'Interest',
      Interest: 'Interest',

      tax: 'Tax',
      Tax: 'Tax',

      pat: 'PAT',
      PAT: 'PAT',

      grossProfit: 'Gross_Profit',
      Gross_Profit: 'Gross_Profit',

      netProfit: 'Net_Profit',
      Net_Profit: 'Net_Profit',

      otherIncome: 'Other_Income',
      Other_Income: 'Other_Income',

      fiscalYear: 'Fiscal_Year',
      Fiscal_Year: 'Fiscal_Year',

      // Other fields
      createdBy: 'Created_By',
      jsonPath: 'JsonPath',
      pdfPath: 'PdfPath',
      createdAt: 'Created_At',
      companyId: 'Company_ID',
      fileId: 'File_ID',
      verificationId: 'Verification_ID'
    };

    // Balance Sheet mapping (DB whitelist names)
    const balanceSheetMap = {
      assessmentYear: 'Assessment_Year',
      companyId: 'Company_ID',
      createdBy: 'Created_By',
      jsonPath: 'JsonPath',
      pdfPath: 'PdfPath',
      fiscalYear: 'Fiscal_Year',
      capital: 'Capital',
      reservesAndSurplusExclRevaluation: 'Reserves_and_Surplus_Excl_Revaluation',
      revaluationReserve: 'Revaluation_Reserve',
      netWorth: 'Net_Worth',
      shortTermDebt: 'Short_Term_Debt',
      otherCurrentLiabilities: 'Other_Current_Liabilities',
      totalCurrentLiabilities: 'Total_Current_Liabilities',
      longTermDebt: 'Long_Term_Debt',
      totalDebt: 'Total_Debt',
      totalLiabilities: 'Total_Liabilities',
      netBlock: 'Net_Block',
      intangibleAssets: 'Intangible_Assets',
      investmentsAndLoansAdvances: 'Investments_and_Loans_Advances',
      totalCurrentAssets: 'Total_Current_Assets',
      nonCurrentAssets: 'Non_Current_Assets',
      totalAssets: 'Total_Assets',
      contingentLiabilities: 'Contingent_Liabilities',
      capitalEmployed: 'Capital_Employed',
      createdAt: 'Created_At',
      fileId: 'File_ID',
      verificationId: 'Verification_ID'
    };

    const gstMap = {
      // Frontend field names to backend field names
      gstin: 'GSTIN',
      GSTIN: 'GSTIN',                           // Database field name as key
      legalNameOfBusiness: 'LegalNameOfBusiness',
      LegalNameOfBusiness: 'LegalNameOfBusiness', // Database field name as key
      tradeName: 'TradeName',
      TradeName: 'TradeName',                   // Database field name as key
      constitutionOfBusiness: 'ConstitutionOfBusiness',
      ConstitutionOfBusiness: 'ConstitutionOfBusiness', // Database field name as key
      address: 'Address',
      Address: 'Address',                       // Database field name as key
      dateOfLiability: 'DateOfLiability',
      DateOfLiability: 'DateOfLiability',       // Database field name as key
      dateOfValidityFrom: 'DateOfValidityFrom',
      DateOfValidityFrom: 'DateOfValidityFrom', // Database field name as key
      dateOfValidityTo: 'DateOfValidityTo',
      DateOfValidityTo: 'DateOfValidityTo',     // Database field name as key
      typeOfRegistration: 'TypeOfRegistration',
      TypeOfRegistration: 'TypeOfRegistration', // Database field name as key
      particularsOfApproving: 'ParticularsOfApproving',
      ParticularsOfApproving: 'ParticularsOfApproving', // Database field name as key
      designationOfApprover: 'DesignationOfApprover',
      DesignationOfApprover: 'DesignationOfApprover', // Database field name as key
      nameOfApprover: 'NameOfApprover',
      NameOfApprover: 'NameOfApprover',         // Database field name as key
      dateOfIssue: 'DateOfIssue',
      DateOfIssue: 'DateOfIssue',               // Database field name as key
      documentType: 'DocumentType',
      DocumentType: 'DocumentType',             // Database field name as key
      jsonPath: 'JsonPath',
      JsonPath: 'JsonPath',                     // Database field name as key
      pdfPath: 'PdfPath',
      PdfPath: 'PdfPath',                       // Database field name as key
      companyId: 'Company_ID',
      Company_ID: 'Company_ID',                 // Database field name as key
      createdBy: 'created_by',
      created_by: 'created_by',                 // Database field name as key
      fileId: 'File_ID',
      File_ID: 'File_ID',                       // Database field name as key
      verificationId: 'Verification_ID',
      Verification_ID: 'Verification_ID'        // Database field name as key
    };

    const udyamMap = {
      udyamRegistrationNumber: 'UdyamRegistrationNumber',
      enterpriseName: 'EnterpriseName',
      typeOfEnterprise: 'TypeOfEnterprise',
      ownerName: 'OwnerName',
      emailId: 'EmailID',
      mobileNumber: 'MobileNumber',
      bankName: 'BankName',
      ifscCode: 'IFSCCode',
      bankAccountNumber: 'BankAccountNumber',
      dateOfIncorporation: 'DateOfIncorporation',
      dateOfCommencement: 'DateOfCommencement',
      dateOfUdyamRegistration: 'DateOfUdyamRegistration',
      documentType: 'DocumentType',
      companyId: 'Company_ID',
      jsonPath: 'JsonPath',
      pdfPath: 'PdfPath',
      fileId: 'File_ID',
      verificationId: 'Verification_ID'
    };

    // Bank Stat (detailed) mapping
    const bankStatMap = {
      companyId: 'Company_ID',
      createdBy: 'Created_By',
      createdAt: 'Created_At',
      totalNoOfCreditTransactions: 'TotalNoOfCreditTransactions',
      totalAmountOfCreditTransactions: 'TotalAmountOfCreditTransactions',
      totalNoOfDebitTransactions: 'TotalNoOfDebitTransactions',
      totalAmountOfDebitTransactions: 'TotalAmountOfDebitTransactions',
      totalNoOfCashDeposits: 'TotalNoOfCashDeposits',
      totalAmountOfCashDeposits: 'TotalAmountOfCashDeposits',
      totalNoOfCashWithdrawals: 'TotalNoOfCashWithdrawals',
      totalAmountOfCashWithdrawals: 'TotalAmountOfCashWithdrawals',
      totalNoOfChequeDeposits: 'TotalNoOfChequeDeposits',
      totalAmountOfChequeDeposits: 'TotalAmountOfChequeDeposits',
      totalNoOfChequeIssues: 'TotalNoOfChequeIssues',
      totalAmountOfChequeIssues: 'TotalAmountOfChequeIssues',
      totalNoOfInwardChequeBounces: 'TotalNoOfInwardChequeBounces',
      totalNoOfOutwardChequeBounces: 'TotalNoOfOutwardChequeBounces',
      minEODBalance: 'MinEODBalance',
      maxEODBalance: 'MaxEODBalance',
      averageEODBalance: 'AverageEODBalance',
      averageCreditTransactionSize: 'AverageCreditTransactionSize',
      averageDebitTransactionSize: 'AverageDebitTransactionSize',
      averageEMITransactionsSize: 'AverageEMITransactionsSize',
      maximumBalance: 'MaximumBalance',
      minimumBalance: 'MinimumBalance',
      fileId: 'File_ID',
      verificationId: 'Verification_ID'
    };

    // Bank Stat Summary mapping (page 19)
    const bankStatSummaryMap = {
      accountHolderName: 'Account_Holder_Name',
      address: 'Address',
      ifsc: 'IFSC',
      micr: 'MICR',
      accountOpeningDate: 'Account_Opening_Date',
      accountCategory: 'Account_Category',
      bankName: 'Bank_Name',
      accountNumber: 'Account_Number',
      source: 'Source',
      bankConnectScore: 'BankConnect_Score',
      odLimit: 'OD_Limit',
      drawingPower: 'Drawing_Power',
      missingData: 'Missing_Data',
      createdAt: 'Created_At',
      companyId: 'Company_ID',
      createdBy: 'created_by',
      jsonPath: 'JsonPath',
      pdfPath: 'PdfPath',
      fileId: 'File_ID',
      verificationId: 'Verification_ID'
    };

    // Move the declaration before the console.logs
    // const fieldToAllowed =
    //   isPan ? panMap :
    //   isAadhaar ? aadhaarMap :
    //   isItr ? itrMap :
    //   isNetWorth ? netWorthMap :
    //   isUdyam ? udyamMap :           // moved up before CIBIL
    //   isCibil ? cibilMap :
    //   isCompanyCibil ? companyCibilMap :
    //   isBalanceSheet ? balanceSheetMap :
    //   isProfitLoss ? profitLossMap :
    //   isBankStat ? bankStatMap :
    //   isBankStatSummary ? bankStatSummaryMap :
    //   isGst ? gstMap :
    //   silverMap;

    const fieldToAllowed =
      isNetWorth ? netWorthMap :       // MUST COME FIRST
        isBalanceSheet ? balanceSheetMap :
          isProfitLoss ? profitLossMap :
            isGst ? gstMap :
              isBankStatSummary ? bankStatSummaryMap :
                isBankStat ? bankStatMap :
                  isItr ? itrMap :
                    isPan ? panMap :
                      isAadhaar ? aadhaarMap :
                        isUdyam ? udyamMap :
                          isCibil ? cibilMap :
                            isCompanyCibil ? companyCibilMap :
                              silverMap;

    console.log(' [SAVE FIELD] Field Mapping:');
    console.log('  - Selected mapping:',
      isNetWorth ? 'netWorthMap' :
        isBalanceSheet ? 'balanceSheetMap' :
          isProfitLoss ? 'profitLossMap' :
            isGst ? 'gstMap' :
              isBankStatSummary ? 'bankStatSummaryMap' :
                isBankStat ? 'bankStatMap' :
                  isItr ? 'itrMap' :
                    isPan ? 'panMap' :
                      isAadhaar ? 'aadhaarMap' :
                        isUdyam ? 'udyamMap' :
                          isCibil ? 'cibilMap' :
                            isCompanyCibil ? 'companyCibilMap' :
                              'silverMap'
    );

    try {
      console.log('  - fieldToAllowed keys:', Object.keys(fieldToAllowed));
    } catch (e) {
      console.error('  - ERROR getting fieldToAllowed keys:', e);
    }

    console.log('  - Looking for field:', field);

    let serverKey;
    try {
      serverKey = fieldToAllowed[field];
      console.log(' [SAVE FIELD] Server Key Lookup:');
      console.log('  - Field:', field);
      console.log('  - Server Key:', serverKey);
    } catch (e) {
      console.error(' [SAVE FIELD] ERROR in Server Key Lookup:', e);
      return;
    }

    if (!serverKey) {
      console.warn(' [SAVE FIELD] Field not updatable via API:', field);
      console.warn(' [SAVE FIELD] Available fields:', Object.keys(fieldToAllowed));
      return;
    }
    try {
      const payload = { [serverKey]: value };
      console.log(' [SAVE FIELD] Preparing to save:');
      console.log('  - Section:', (isProfitLoss && 'profit_loss') || (isBalanceSheet && 'balance_sheet') || (isGst && 'gst') || (isUdyam && 'udyam') || (isCibil && 'cibil') || (isCompanyCibil && 'company_cibil') || (isBankStat && 'bankstat') || (isBankStatSummary && 'bankstat_summary') || (isItr && 'itr') || (isPan && 'pan') || (isAadhaar && 'aadhaar') || 'silver');
      console.log('  - AppId:', appId);
      console.log('  - Field:', field);
      console.log('  - Server Key:', serverKey);
      console.log('  - Value:', value);
      console.log('  - Payload:', payload);
      console.log('  - saveAdapter type:', typeof saveAdapter);
      console.log('  - saveAdapter function:', saveAdapter);

      // CRITICAL DEBUG: Log the exact API call details
      console.log(' [CRITICAL DEBUG] API Call Details:');
      console.log('  - API Function: updateProfitLoss (expected)');
      console.log('  - Expected URL: /api/silver/profit-loss/' + appId);
      console.log('  - Expected Method: PUT');
      console.log('  - Expected Payload:', JSON.stringify(payload));

      // DON'T mark as edited here - it's already marked in handleChange!
      // Just save

      let resp;
      if (typeof saveAdapter === 'function') {
        console.log('🟢 [SAVE FIELD] Using saveAdapter function');
        try {
          resp = await saveAdapter(appId, payload);
          console.log('🟢 [SAVE FIELD] ✅ SaveAdapter completed successfully');
          console.log('🟢 [SAVE FIELD] SaveAdapter response:', JSON.stringify(resp, null, 2));
          // ✅ Mark field as saved HERE (after saveAdapter completes) so handleBlur toast works
          lastSavedRef.current[field] = value;
        } catch (saveAdapterError) {
          console.error('🟢 [SAVE FIELD] ❌ SaveAdapter failed:', saveAdapterError);
          throw saveAdapterError;
        }
      } else {
        console.log('🟢 [SAVE FIELD] Using default updateSilverLoanApplication');
        resp = await updateSilverLoanApplication(appId, payload);
      }
      console.log('🔴 [SAVE FIELD]E Save response:', resp);

      // ✅ GUARANTEED FIX: saveAdapter response se updated values formData mein sync karo
      // Yeh ensure karta hai ki DB mein jo save hua woh UI mein dikhega
      // Aur formPopulatedRef = true rehta hai toh future resets bhi nahi honge
      if (resp && resp.success && resp.data && typeof resp.data === 'object') {
        console.log('🟢 [SAVE FIELD] ✅ Syncing formData from saveAdapter response');
        setFormData((prev) => {
          const updated = { ...prev };
          Object.keys(prev).forEach((existingKey) => {
            const respKeys = Object.keys(resp.data);
            const matchedKey = respKeys.find(
              (rk) => rk.toLowerCase() === existingKey.toLowerCase()
            );
            if (matchedKey !== undefined && resp.data[matchedKey] !== null && resp.data[matchedKey] !== undefined) {
              const rawVal = resp.data[matchedKey];
              if (typeof rawVal === 'object') {
                updated[existingKey] = JSON.stringify(rawVal);
              } else {
                // ✅ FIX: ISO date format → DD/MM/YYYY (same as safeProcessValue)
                const strVal = String(rawVal);
                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(strVal)) {
                  try {
                    const d = new Date(strVal);
                    if (!isNaN(d.getTime())) {
                      const day = String(d.getUTCDate()).padStart(2, '0');
                      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
                      const year = d.getUTCFullYear();
                      updated[existingKey] = `${day}/${month}/${year}`;
                    } else {
                      updated[existingKey] = strVal;
                    }
                  } catch {
                    updated[existingKey] = strVal;
                  }
                } else {
                  updated[existingKey] = strVal;
                }
              }
            }
          });
          console.log('🟢 [SAVE FIELD] Updated formData keys from response:', Object.keys(updated));

          // ✅ KEY FIX: Snapshot update karo with latest saved values
          // Yeh ensure karta hai ki jab page navigate karke wapas aayein
          // toh saved values milein, na ki original DB values
          const currentIdentity = currentDataIdentity.current;
          if (currentIdentity) {
            formDataSnapshotRef.current[currentIdentity] = updated;
            console.log('🟢 [SAVE FIELD] ✅ Snapshot updated for:', currentIdentity);
          }

          return updated;
        });
      } else {
        // Fallback: sirf edited field update karo
        setFormData((prev) => {
          const updated = { ...prev, [field]: value };
          // ✅ Snapshot update karo fallback case mein bhi
          const currentIdentity = currentDataIdentity.current;
          if (currentIdentity) {
            formDataSnapshotRef.current[currentIdentity] = updated;
          }
          return updated;
        });
      }

      console.log('� [SASVE FIELD] ✅ Field saved successfully');
      // NOTE: editedFields will be cleared in handleChange's .then()

      // 🔥 FIX: Removed setForceRender to prevent TextField recreation
      // setForceRender(prev => prev + 1);

      // Force exit editing mode after a small delay to show updated display value
      // setTimeout(() => {
      //   setEditingField(null);
      //   setHighlightField(null);  // Also clear highlight
      //   console.log(' [SAVE FIELD] Exited editing mode to show updated value');
      // }, 100);
      console.log(' [SAVE FIELD] Local state updated with value:', value);

      // OPTIMIZED: Removed automatic onSaved callback after every field save
      // This was causing excessive API calls to refresh data after each field change
      // The data refresh is now only done when explicitly needed (e.g., after "Confirm" button)
      // Local state is updated immediately, so UI shows the new value without needing a refresh

      console.log(' [SAVE FIELD] ✅ Field saved successfully');
    } catch (e) {
      console.error(' [SAVE FIELD] Save failed:', e?.message || e);
      console.error(' [SAVE FIELD] Full error:', e);
      // Intentionally do not refresh on error
    }
  };

  // Parse date string to dayjs object
  // Parse date string to dayjs object
  const parseDateValue = (value) => {
    if (!value || typeof value === 'object') return null;

    // Handle ISO format dates (like 2025-09-29T00:00:00.000Z)
    if (value.includes('T') && value.includes('Z')) {
      const parsed = dayjs(value);
      if (parsed.isValid()) return parsed;
    }

    // Try DD/MM/YYYY format first
    let parsed = dayjs(value, 'DD/MM/YYYY', true);
    if (parsed.isValid()) return parsed;

    // Try YYYY-MM-DD format
    parsed = dayjs(value, 'YYYY-MM-DD', true);
    if (parsed.isValid()) return parsed;

    // Try MM/DD/YYYY format
    parsed = dayjs(value, 'MM/DD/YYYY', true);
    if (parsed.isValid()) return parsed;

    // Try ISO format
    parsed = dayjs(value);
    if (parsed.isValid()) return parsed;

    return null;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          flex: 1,
          height: "100%",
          backgroundColor: "white",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 1,
          display: "flex",
          flexDirection: "column",
          maxHeight: "100%"
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
            minHeight: "auto"
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e4258" }}>
            Extracted Data/Manual Review
          </Typography>
        </Box>

        {/* Form Content */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            overflowY: "scroll",
            overflowX: "hidden",
            minHeight: 0,
            maxHeight: "100%",
            WebkitOverflowScrolling: "touch"
          }}
        >
          {Object.keys(formData).length === 0 ? (
            <Box sx={{
              height: '100%',
              minHeight: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af'
            }}>
              <Typography variant="body1">No extracted data found for this application.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {Object.entries(formData).map(([field, value]) => {
                // Debug logging for each field render
                if (field === 'totalAssets') {
                  console.log(` [RENDER] Rendering ${field} with value:`, value);
                  console.log(` [RENDER] formData[${field}]:`, formData[field]);
                }

                // 🔥 FIX: Create field handlers for each field
                const fieldHandlers = {
                  onChange: (e) => {
                    const newValue = e.target.value;
                    console.log(`📝 [FIELD HANDLER] onChange for ${field}:`, newValue);
                    handleChange(field, newValue);
                  },
                  onKeyPress: (e) => {
                    console.log(`⌨️ [FIELD HANDLER] onKeyPress for ${field}:`, e.key);
                    handleKeyPress(e, field);
                  },
                  onBlur: () => {
                    console.log(`👋 [FIELD HANDLER] onBlur for ${field}`);
                    handleBlur(field);
                  },
                  onEditClick: () => {
                    console.log(`✏️ [FIELD HANDLER] onEditClick for ${field}`);
                    // For date fields, open the date picker instead of edit mode
                    if (isDateField(field)) {
                      console.log(`📅 [FIELD HANDLER] Opening date picker for ${field}`);
                      setDatePickerOpen(field);
                      setHighlightField(field);
                    } else {
                      handleEdit(field);
                    }
                  },
                  onDateChange: (newValue) => {
                    console.log(`📅 [FIELD HANDLER] onDateChange for ${field}:`, newValue);
                    if (newValue && newValue.isValid()) {
                      const formatted = newValue.format('DD/MM/YYYY');
                      console.log(`📅 [FIELD HANDLER] Formatted date:`, formatted);
                      handleChange(field, formatted);
                      setDatePickerOpen(null);
                    }
                  }
                };

                return (
                  <Box key={field}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 56,
                        border: 1,
                        borderColor: errors[field]
                          ? "#d32f2f"
                          : highlightField === field 
                            ? "primary.main" 
                            : confidenceScores && confidenceScores[field] !== undefined
                              ? (confidenceScores[field] >= 100 ? "#10b98120" : confidenceScores[field] >= 66 ? "#f59e0b20" : "#ef444420")
                              : "#d0d7de",
                        borderRadius: 2,
                        backgroundColor: errors[field]
                          ? "#fff5f5"
                          : highlightField === field 
                            ? "#f0f7ff" 
                            : confidenceScores && confidenceScores[field] !== undefined
                              ? (confidenceScores[field] >= 100 ? "#f0fdf410" : confidenceScores[field] >= 66 ? "#fffbeb10" : "#fef2f210")
                              : "#fff",
                        boxShadow: errors[field]
                          ? "0 0 0 2px rgba(211, 47, 47, 0.1)"
                          : highlightField === field
                            ? "0 0 0 2px rgba(25, 118, 210, 0.2)"
                            : confidenceScores && confidenceScores[field] !== undefined
                              ? (confidenceScores[field] >= 100 
                                  ? "0 1px 3px rgba(16, 185, 129, 0.08)" 
                                  : confidenceScores[field] >= 66 
                                    ? "0 1px 3px rgba(245, 158, 11, 0.08)" 
                                    : "0 1px 3px rgba(239, 68, 68, 0.08)")
                              : "none",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          borderColor: errors[field] ? "#d32f2f" : "primary.main"
                        }
                      }}
                    >
                      {/* Label Section */}
                      <Box
                        sx={{
                          width: 180,
                          p: 1.5,
                          borderRight: "1px solid #2196f3",
                          display: "flex",
                          alignItems: "center",
                          borderColor: "#fff"
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#2e4258", fontSize: "14px" }}
                        >
                          {fieldLabels[field] || formatLabel(field)}
                        </Typography>
                      </Box>

                      {/* Value Section */}
                      <Box
                        sx={{
                          flex: 1,
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          minHeight: 40
                        }}
                      >
                        {editingField === field ? (
                          <TextField
                            key={`edit-${field}-${formData[field]}`} // 🔥 FIX: Stable key to prevent TextField recreation
                            fullWidth
                            variant="standard"
                            value={typeof value === 'object' && value !== null ? JSON.stringify(value) : (value ?? '')}
                            onChange={fieldHandlers.onChange}
                            onKeyPress={fieldHandlers.onKeyPress}
                            onBlur={fieldHandlers.onBlur}
                            autoFocus
                            multiline={field.toLowerCase().includes("address")}
                            maxRows={4}
                            inputRef={(el) => {
                              if (el) {
                                inputRefs.current[field] = el;
                              }
                            }}
                            InputProps={{
                              disableUnderline: true,
                              sx: {
                                fontSize: "14px",
                                lineHeight: 1.5,
                                padding: 0,
                                "& .MuiInputBase-input": {
                                  padding: 0,
                                  backgroundColor: "transparent",
                                  color: errors[field] ? "#d32f2f" : "inherit"
                                }
                              }
                            }}
                          />
                        ) : datePickerOpen === field ? (
                          <DatePicker
                            value={parseDateValue(value)}
                            onChange={fieldHandlers.onDateChange}
                            open={true}
                            onClose={datePickerCloseHandler}
                            format="DD/MM/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                variant: "standard",
                                InputProps: {
                                  disableUnderline: true,
                                  sx: {
                                    fontSize: "14px",
                                    lineHeight: 1.5,
                                    padding: 0
                                  }
                                }
                              },
                              openPickerButton: {
                                sx: { display: 'none' }
                              }
                            }}
                          />

                        ) : (
                          <Typography
                            key={`display-${field}-${formData[field]}`} // 🔥 FIX: Stable key to prevent Typography recreation
                            variant="body2"
                            sx={{
                              flex: 1,
                              color: "#333",
                              lineHeight: 1.4,
                              fontSize: "13px",
                              whiteSpace: field.toLowerCase().includes("address") ? "pre-line" : "normal"
                            }}
                          >
                            {typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : (value ?? '')}
                          </Typography>
                        )}
                      </Box>

                      {/* Action Buttons Section */}
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1.5, 
                        pr: 1.5,
                        pl: 1
                      }}>
                        {/* Confidence Badge - only show for loan application fields */}
                        {confidenceScores && confidenceScores[field] !== undefined && (
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            animation: 'slideInRight 0.3s ease-out',
                            '@keyframes slideInRight': {
                              '0%': {
                                opacity: 0,
                                transform: 'translateX(-10px)',
                              },
                              '100%': {
                                opacity: 1,
                                transform: 'translateX(0)',
                              },
                            },
                          }}>
                            <ConfidenceBadge score={confidenceScores[field]} />
                          </Box>
                        )}
                        
                        <IconButton
                          size="small"
                          onMouseDown={(e) => {
                            // Prevent blur from triggering
                            e.preventDefault();
                            editButtonClickRef.current = true;
                          }}
                          onClick={(e) => {
                            console.log('🖱️ [EDIT BUTTON] Clicked for field:', field);
                            editButtonClickRef.current = false;
                            // 🔥 FIX: Call inline fieldHandlers.onEditClick directly
                            fieldHandlers.onEditClick();
                          }}
                          sx={{
                            color: (editingField === field || datePickerOpen === field) ? "primary.main" : "#666",
                            transition: 'all 0.2s ease',
                            "&:hover": {
                              backgroundColor: "rgba(33, 150, 243, 0.08)",
                              color: "primary.main",
                              transform: 'scale(1.1)',
                            }
                          }}
                        >
                          {isDateField(field) ? (
                            <CalendarToday sx={{ fontSize: 16 }} />
                          ) : (
                            <Edit sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Box>
                    </Box>

                    {/*  Error message outside the box */}
                    {errors[field] && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#d32f2f",
                          fontSize: "12px",
                          marginTop: "4px",
                          marginLeft: "12px",
                          display: "block",
                          fontWeight: 400
                        }}
                      >
                        {errors[field]}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>

      {/* Saved Toast Popup */}
      {savedToast && (
        <Box
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 9999,
            animation: 'slideInRight 0.3s ease',
            '@keyframes slideInRight': {
              from: { opacity: 0, transform: 'translateX(60px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #16a34a',
              borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              p: '14px 18px',
              minWidth: 260,
              maxWidth: 320,
            }}
          >
            {/* Green check icon */}
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: '1px',
              }}
            >
              <Typography sx={{ fontSize: 16, lineHeight: 1 }}>✓</Typography>
            </Box>

            {/* Text content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#15803d',
                  lineHeight: 1.3,
                  mb: '4px',
                }}
              >
                Field Updated
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#64748b',
                  lineHeight: 1.3,
                  mb: '4px',
                }}
              >
                <Box component="span" sx={{ fontWeight: 600, color: '#334155' }}>
                  {savedToast.label}
                </Box>{' '}
                has been saved
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#94a3b8',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                }}
              >
                Value: {savedToast.value}
              </Typography>
            </Box>

            {/* Close button */}
            <Box
              onClick={() => setSavedToast(null)}
              sx={{
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: '16px',
                lineHeight: 1,
                flexShrink: 0,
                '&:hover': { color: '#475569' },
              }}
            >
              ×
            </Box>
          </Box>

          {/* Progress bar */}
          <Box
            sx={{
              height: 3,
              backgroundColor: '#dcfce7',
              borderRadius: '0 0 10px 10px',
              overflow: 'hidden',
              mx: '1px',
            }}
          >
            <Box
              sx={{
                height: '100%',
                backgroundColor: '#16a34a',
                animation: 'shrink 3s linear forwards',
                '@keyframes shrink': {
                  from: { width: '100%' },
                  to: { width: '0%' },
                },
              }}
            />
          </Box>
        </Box>
      )}

    </LocalizationProvider>
  );
};

export default ExtractedData;
